import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, Stack, Group, Text, Badge, Switch, Button } from '@mantine/core';
import { collection, doc, onSnapshot, query, setDoc, Timestamp, where, writeBatch, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTranslation } from 'react-i18next';

// Admin editor to view/edit availabilities for a single event across all members.
export default function AdminEventAvailabilities({ planningId, event, members = [] }) {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const effectiveDb = db;
  const eventId = event?.id;

  const [byUser, setByUser] = useState(new Map()); // uid -> { available: bool, updatedAt }

  useEffect(() => {
    if (!planningId || !eventId) return;
    const col = collection(effectiveDb, 'plannings', planningId, 'availabilities');
    const q = query(col, where('eventId', '==', eventId));
    const unsub = onSnapshot(q, (snap) => {
      const map = new Map();
      for (const d of snap.docs) {
        const data = d.data() || {};
        const uid = data.userId;
        if (uid) map.set(uid, { available: !!data.available, updatedAt: data.updatedAt });
      }
      setByUser(map);
    });
    return () => unsub();
  }, [effectiveDb, planningId, eventId]);

  const setAvailability = useCallback(async (uid, available) => {
    if (!planningId || !eventId || !uid) return;
    const id = `${eventId}_${uid}`;
    const ref = doc(collection(effectiveDb, 'plannings', planningId, 'availabilities'), id);
    // Ensure rules see this uid as a known participant when owner writes on behalf of someone else
    try { await updateDoc(doc(effectiveDb, 'plannings', planningId), { participants: arrayUnion(uid) }); } catch {}
    await setDoc(ref, { eventId, userId: uid, seriesId: event?.seriesId || null, available: !!available, updatedAt: Timestamp.now() }, { merge: true });
  }, [effectiveDb, planningId, eventId, event?.seriesId]);

  const allAvailable = useMemo(() => members.every((m) => byUser.get(m.value)?.available === true), [members, byUser]);

  const toggleAll = async (val) => {
    if (!planningId || !eventId) return;
    setLoading(true);
    try {
      const col = collection(effectiveDb, 'plannings', planningId, 'availabilities');
      const batch = writeBatch(effectiveDb);
      const now = Timestamp.now();
      for (const m of members) {
        const id = `${eventId}_${m.value}`;
        const ref = doc(col, id);
        try { await updateDoc(doc(effectiveDb, 'plannings', planningId), { participants: arrayUnion(m.value) }); } catch {}
        batch.set(ref, { eventId, userId: m.value, seriesId: event?.seriesId || null, available: !!val, updatedAt: now }, { merge: true });
      }
      await batch.commit();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card withBorder p="md">
      <Stack gap={6}>
        <Group justify="space-between" align="center">
          <Text fw={600}>{t('availability.admin.title')}</Text>
          <Group gap="xs">
            <Badge variant="light">{t('availability.admin.members_count', { count: members.length })}</Badge>
            <Badge variant="light" color={allAvailable ? 'green' : 'gray'}>
              {allAvailable ? t('availability.admin.all_available') : t('availability.admin.mixed')}
            </Badge>
          </Group>
        </Group>
        <Group justify="space-between">
          <Button size="xs" variant="light" onClick={() => toggleAll(true)} loading={loading}>{t('availability.admin.mark_all_available')}</Button>
          <Button size="xs" variant="light" color="red" onClick={() => toggleAll(false)} loading={loading}>{t('availability.admin.mark_all_unavailable')}</Button>
        </Group>
        <Stack gap={4}>
          {members.map((m) => {
            const rec = byUser.get(m.value);
            const val = rec ? !!rec.available : false;
            return (
              <Group key={m.value} justify="space-between" align="center">
                <Text>{m.label || m.value}</Text>
                <Switch checked={val} onChange={(e) => setAvailability(m.value, e.currentTarget.checked)} />
              </Group>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}
